/**
 * Pressure control (fieldiq-api#1268, BOP-1) — the rules every pressure-control
 * screen shares: who may do what, the labels, and how the API's certificate
 * state is put into words.
 *
 * ROLES, as the API gates them (lib/pressure-control/roles.ts at eb686f61):
 *   read      admin, office_staff, supervisor, dispatcher, field_worker, logistics
 *   admin     admin, office_staff — settings, specs, certificates
 *   events    admin, office_staff, supervisor — service events
 * Both write gates are NON-strict requireRole lists that include 'admin', so a
 * dispatcher on a tenant with dispatcher_admin_access is let in too. The server
 * is the authority; these only decide which controls to draw.
 *
 * NOTHING HERE COMPUTES A DATE OR A DAY COUNT. The status, the last valid day,
 * the days remaining and the reason's basis are the API's certState; this file
 * only chooses the words. A field the API did not return renders `—`.
 */
import type {
  PcCalibration,
  PcCertification,
  PcCertState,
  PcComponent,
  PcComponentClass,
  PcComponentDetail,
  PcDrill,
  PcDrillStanding,
  PcDrillStatus,
  PcDrillKind,
  PcDrillWarning,
  PcExtension,
  PcGaugeCalibration,
  PcModuleStatus,
  PcServiceEvent,
  PcSpec,
  PcTest,
  PcTestDefault,
  PcTestItem,
  PcTestKind,
  PcReviewStatus,
} from '../types/pressure-control';
import { formatCalendarDate } from './calendar-date';
import { fmtQty } from './display-labels';

/** The register's route; a component's page is `${PC_LIST}/:equipmentId`. */
export const PC_LIST = '/equipment/pressure-control';

export const PC_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const PC_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const PC_EVENT_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[], role: string | null | undefined) => !!role && list.includes(role);

export function canReadPc(role: string | null | undefined): boolean {
  return inList(PC_READ_ROLES, role);
}

/** Settings, registering a component, editing its spec, recording a certificate. */
export function canAdminPc(role: string | null | undefined, dispatcherElevated = false): boolean {
  if (inList(PC_ADMIN_ROLES, role)) return true;
  return role === 'dispatcher' && dispatcherElevated;
}

/** Recording a service event. */
export function canRecordPcEvent(role: string | null | undefined, dispatcherElevated = false): boolean {
  if (inList(PC_EVENT_ROLES, role)) return true;
  return role === 'dispatcher' && dispatcherElevated;
}

// ─── labels ──────────────────────────────────────────────────────────────────

export const PC_CLASS_LABEL: Record<string, string> = {
  annular: 'Annular',
  pipe_ram: 'Pipe ram',
  blind_ram: 'Blind ram',
  blind_shear_ram: 'Blind shear ram',
  spool: 'Spool',
  adapter_flange: 'Adapter flange',
  ram_block: 'Ram block',
  carrier: 'Carrier',
  bleed_off_hose: 'Bleed-off hose',
  kill_hose: 'Kill hose',
  choke_manifold: 'Choke manifold',
  accumulator: 'Accumulator',
  stack_assembly: 'Stack assembly',
  other: 'Other',
  // Wireline & Slickline (api#1333, WL-0).
  lubricator: 'Lubricator',
  grease_injection_head: 'Grease injection head',
  stuffing_box: 'Stuffing box',
  pack_off: 'Pack-off',
  line_wiper: 'Line wiper',
  wireline_valve: 'Wireline valve',
  pump_in_sub: 'Pump-in sub',
  tool_trap: 'Tool trap',
  tool_catcher: 'Tool catcher',
  wellhead_adapter: 'Wellhead adapter',
} satisfies Record<PcComponentClass, string>;

/** An unknown class reads `—`, never a guessed label. */
export const pcClassLabel = (k: string | null | undefined) => (k && PC_CLASS_LABEL[k]) || '—';

export const PC_STATUS_LABEL: Record<string, string> = {
  uncertified: 'Never certified',
  recert_required: 'Recert required',
  expired: 'Expired',
  due_soon: 'Due soon',
  in_storage: 'In storage',
  valid: 'Valid',
};

/**
 * Saturated fill, dark ink. Five hues, six statuses: expired and recert
 * required share red (both mean "cannot go on a well"), and the label tells
 * them apart. Never certified is slate — the absence of a certificate, named —
 * and an unknown status is slate reading `—`.
 */
export const PC_STATUS_FILL: Record<string, string> = {
  uncertified: 'bg-fi-signal-slate',
  recert_required: 'bg-fi-signal-red',
  expired: 'bg-fi-signal-red',
  due_soon: 'bg-fi-signal-amber',
  in_storage: 'bg-fi-signal-blue',
  valid: 'bg-fi-signal-green',
};

export const PC_STATUS_RAIL: Record<string, string> = {
  uncertified: 'border-l-fi-steel',
  recert_required: 'border-l-fi-red',
  expired: 'border-l-fi-red',
  due_soon: 'border-l-fi-amber',
  in_storage: 'border-l-fi-blue',
  valid: 'border-l-fi-green',
};

export const PC_EVENT_LABEL: Record<string, string> = {
  placed_in_service: 'Placed in service',
  returned_to_storage: 'Returned to storage',
  elastomers_installed: 'Elastomers installed',
  recert_trigger: 'Recert trigger',
};

export const PC_TRIGGER_LABEL: Record<string, string> = {
  blowout: 'Blowout',
  over_pressure: 'Over-pressure',
  sour_exposure: 'Sour exposure',
  well_control_event: 'Well control event',
};

export const pcEventLabel = (k: string | null | undefined) => (k && PC_EVENT_LABEL[k]) || '—';
export const pcTriggerLabel = (k: string | null | undefined) => (k && PC_TRIGGER_LABEL[k]) || '—';

/** A calendar day as "Mar 23, 2026", or `—`. */
export const pcDay = (d: string | null | undefined) => formatCalendarDate(d ?? null) ?? '—';

// ─── reading (camelCase, tolerating snake_case) ──────────────────────────────

type Raw = Record<string, any> | null | undefined;
const pick = (o: Raw, camel: string, snake: string) => (o ? (o[camel] !== undefined ? o[camel] : o[snake]) : undefined);
const orNull = <T,>(v: T | undefined): T | null => (v === undefined ? null : v);
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function readCertState(raw: Raw): PcCertState | null {
  if (!raw) return null;
  const triggers: any[] = pick(raw, 'recertTriggers', 'recert_triggers') ?? [];
  return {
    status: pick(raw, 'status', 'status') ?? null,
    inService: !!pick(raw, 'inService', 'in_service'),
    clockStartedOn: orNull(pick(raw, 'clockStartedOn', 'clock_started_on')),
    elastomerRule: !!pick(raw, 'elastomerRule', 'elastomer_rule'),
    expiresOn: orNull(pick(raw, 'expiresOn', 'expires_on')),
    baseExpiresOn: orNull(pick(raw, 'baseExpiresOn', 'base_expires_on')),
    extendedThrough: orNull(pick(raw, 'extendedThrough', 'extended_through')),
    expiryBasis: orNull(pick(raw, 'expiryBasis', 'expiry_basis')),
    daysUntilExpiry: numOrNull(pick(raw, 'daysUntilExpiry', 'days_until_expiry')),
    recertTriggers: Array.isArray(triggers)
      ? triggers.map((t) => ({ reason: t?.reason ?? null, occurredOn: pick(t, 'occurredOn', 'occurred_on') ?? null }))
      : [],
  };
}

export function readCertification(raw: Raw): PcCertification | null {
  if (!raw) return null;
  return {
    id: raw.id,
    certifiedOn: pick(raw, 'certifiedOn', 'certified_on') ?? null,
    inServiceOn: orNull(pick(raw, 'inServiceOn', 'in_service_on')),
    certifyingFacility: orNull(pick(raw, 'certifyingFacility', 'certifying_facility')),
    certifyingEngineer: orNull(pick(raw, 'certifyingEngineer', 'certifying_engineer')),
    certificateNumber: orNull(pick(raw, 'certificateNumber', 'certificate_number')),
    documentUploadId: orNull(pick(raw, 'documentUploadId', 'document_upload_id')),
    maxInServiceYears: numOrNull(pick(raw, 'maxInServiceYears', 'max_in_service_years')) as number,
    maxTotalYears: numOrNull(pick(raw, 'maxTotalYears', 'max_total_years')) as number,
    notes: orNull(pick(raw, 'notes', 'notes')),
    createdBy: orNull(pick(raw, 'createdBy', 'created_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
  };
}

export function readEvent(raw: Raw): PcServiceEvent | null {
  if (!raw) return null;
  return {
    id: raw.id,
    eventKind: pick(raw, 'eventKind', 'event_kind') ?? null,
    triggerReason: orNull(pick(raw, 'triggerReason', 'trigger_reason')),
    occurredOn: pick(raw, 'occurredOn', 'occurred_on') ?? null,
    contextRef: orNull(pick(raw, 'contextRef', 'context_ref')),
    notes: orNull(pick(raw, 'notes', 'notes')),
    recordedBy: orNull(pick(raw, 'recordedBy', 'recorded_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
  };
}

function readSpec(raw: Raw): PcSpec {
  const s = raw ?? {};
  return {
    id: s.id ?? null,
    componentClass: pick(s, 'componentClass', 'component_class') ?? null,
    hasElastomers: !!pick(s, 'hasElastomers', 'has_elastomers'),
    boreSize: orNull(pick(s, 'boreSize', 'bore_size')),
    workingPressure: numOrNull(pick(s, 'workingPressure', 'working_pressure')),
    pressureUnit: pick(s, 'pressureUnit', 'pressure_unit') ?? '',
    naceMr0175: !!pick(s, 'naceMr0175', 'nace_mr0175'),
    stackParentId: orNull(pick(s, 'stackParentId', 'stack_parent_id')),
    stackParentName: orNull(pick(s, 'stackParentName', 'stack_parent_name')),
    notes: orNull(pick(s, 'notes', 'notes')),
    createdAt: orNull(pick(s, 'createdAt', 'created_at')),
    updatedAt: orNull(pick(s, 'updatedAt', 'updated_at')),
  };
}

export function readComponent(raw: Raw): PcComponent | null {
  if (!raw) return null;
  const equipmentId = pick(raw, 'equipmentId', 'equipment_id');
  if (!equipmentId) return null;
  return {
    equipmentId,
    name: raw.name ?? null,
    type: orNull(raw.type),
    unitNumber: orNull(pick(raw, 'unitNumber', 'unit_number')),
    serialNumber: orNull(pick(raw, 'serialNumber', 'serial_number')),
    manufacturer: orNull(raw.manufacturer),
    model: orNull(raw.model),
    equipmentStatus: pick(raw, 'equipmentStatus', 'equipment_status') ?? null,
    spec: readSpec(raw.spec),
    currentCertification: readCertification(pick(raw, 'currentCertification', 'current_certification')),
    certState: readCertState(pick(raw, 'certState', 'cert_state')) as PcCertState,
  };
}

export function readComponentDetail(raw: Raw): PcComponentDetail | null {
  const base = readComponent(raw);
  if (!base || !raw) return null;
  const certs: any[] = raw.certifications ?? [];
  const events: any[] = raw.events ?? [];
  const kids: any[] = pick(raw, 'stackComponents', 'stack_components') ?? [];
  return {
    ...base,
    certifications: certs.map(readCertification).filter(Boolean) as PcCertification[],
    events: events.map(readEvent).filter(Boolean) as PcServiceEvent[],
    stackComponents: kids.map((k) => ({
      equipmentId: pick(k, 'equipmentId', 'equipment_id'),
      name: k?.name ?? null,
      componentClass: pick(k, 'componentClass', 'component_class') ?? null,
    })),
  };
}

// ─── the words ───────────────────────────────────────────────────────────────

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The API's `daysUntilExpiry`, said. Negative is past the last valid day; the
 * sign only chooses the wording — the number is the API's. Absent is `—`.
 */
export function pcDaysPhrase(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return '—';
  if (days === 0) return 'Last valid day today';
  if (days > 0) return `${plural(days, 'day', 'days')} left`;
  return `${plural(-days, 'day', 'days')} past`;
}

/**
 * Why the certificate is in the state it is in, in one or two sentences. Built
 * from the API's certState and the current certificate's own snapshotted year
 * limits — never from today's settings, which may have changed since.
 */
export function pcReason(state: PcCertState | null, cert: PcCertification | null): string {
  if (!state) return '—';
  if (state.status === 'uncertified' || !cert) {
    return 'No certificate on file. It cannot go on a well until one is recorded.';
  }
  if (state.status === 'recert_required') {
    const list = state.recertTriggers
      .map((t) => `${pcTriggerLabel(t.reason).toLowerCase()} on ${pcDay(t.occurredOn)}`)
      .join(', ');
    return `Recertification required since the last certificate: ${list || '—'}.`;
  }
  let basis: string;
  if (state.expiryBasis === 'total_life') {
    const yrs = Number.isFinite(cert.maxTotalYears) ? `${cert.maxTotalYears}-year` : '—';
    basis = `${yrs} elastomer cap, counted from certification on ${pcDay(cert.certifiedOn)}`;
  } else if (state.expiryBasis === 'in_service') {
    const yrs = Number.isFinite(cert.maxInServiceYears) ? `${cert.maxInServiceYears}-year` : '—';
    basis = `${yrs} in-service limit, counted from ${pcDay(state.clockStartedOn)}`;
  } else {
    return 'Certified and not yet placed in service, so no expiry clock is running.';
  }
  const last = pcDay(state.expiresOn);
  // BOP-2: a D036 extension moved the in-service expiry. Say both days, so the
  // reader sees it is extended and that a NEW well is checked against the base.
  if (state.extendedThrough && state.baseExpiresOn) {
    basis = `${basis}; extended under D036 from ${pcDay(state.baseExpiresOn)} for the well operation in progress`;
  }
  if (state.status === 'expired') return `Expired: the last valid day was ${last} (${basis}).`;
  if (state.status === 'in_storage') {
    return `In storage. The clock keeps running in storage: valid through ${last} (${basis}).`;
  }
  return `Valid through ${last} (${basis}).`;
}

/**
 * A refused write in a sentence a reader can act on. A 403 is a permission
 * sentence whatever the body says; otherwise the API's own `error` (its 400s
 * name the field), then `fallback`.
 */
export function pcErrorMessage(error: any, fallback: string): string {
  const status: number | undefined = error?.response?.status;
  if (status === 403) return 'Your role cannot make this change. Ask an admin or office staff.';
  const msg = error?.response?.data?.error;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
}

// ─── BOP-2 (api#1281): tests, drills, calibrations, defaults, extensions ─────

export const PC_TESTS = `${PC_LIST}/tests`;
export const PC_DRILLS = `${PC_LIST}/drills`;
export const PC_CALIBRATIONS = `${PC_LIST}/calibrations`;

export const PC_TEST_KIND_LABEL: Record<string, string> = {
  installation: 'Installation',
  periodic: 'Periodic',
  after_repair: 'After repair',
  elastomers_installed: 'Elastomers installed',
  other: 'Other',
  rig_up: 'Rig-up pressure test',
} satisfies Record<PcTestKind, string>;
export const pcTestKindLabel = (k: string | null | undefined) => (k && PC_TEST_KIND_LABEL[k]) || '—';

export const PC_DRILL_KIND_LABEL: Record<string, string> = {
  drilling: 'Drilling',
  tripping: 'Tripping',
  out_of_hole: 'Out of hole',
  verbal: 'Verbal',
  other: 'Other',
  wireline_shut_in: 'Wireline shut-in drill',
} satisfies Record<PcDrillKind, string>;
export const pcDrillKindLabel = (k: string | null | undefined) => (k && PC_DRILL_KIND_LABEL[k]) || '—';

/** Pass / fail, as a badge. An unknown result is slate reading `—`. */
export const PC_RESULT_LABEL: Record<string, string> = { pass: 'Pass', fail: 'Fail' };

/**
 * A record's review state (fieldiq-api#1324, 0605). `pending` is a test or drill
 * a rig manager or driller recorded on a Well Servicing tour sheet: it counts
 * toward nothing until the office approves the sheet, which approves it too.
 * Every other record is `approved`. An absent or unknown value is the slate `—`.
 */
export const PC_REVIEW_LABEL: Record<PcReviewStatus, string> = { pending: 'Pending', approved: 'Approved' };
export const PC_REVIEW_FILL: Record<PcReviewStatus, string> = { pending: 'bg-fi-signal-amber', approved: 'bg-fi-signal-green' };
export function pcReviewSkin(status: string | null | undefined): { label: string; fill: string } {
  const k = status as PcReviewStatus;
  return status && k in PC_REVIEW_LABEL ? { label: PC_REVIEW_LABEL[k], fill: PC_REVIEW_FILL[k] } : { label: '—', fill: 'bg-fi-signal-slate' };
}
export const PC_RESULT_FILL: Record<string, string> = { pass: 'bg-fi-signal-green', fail: 'bg-fi-signal-red' };
export const PC_RESULT_RAIL: Record<string, string> = { pass: 'border-l-fi-green', fail: 'border-l-fi-red' };

/** Gauge calibration status. "Never calibrated" is slate — the absence, named. */
export const PC_CAL_LABEL: Record<string, string> = {
  valid: 'Calibrated',
  due_soon: 'Due soon',
  overdue: 'Overdue',
  never: 'Never calibrated',
};
export const PC_CAL_FILL: Record<string, string> = {
  valid: 'bg-fi-signal-green',
  due_soon: 'bg-fi-signal-amber',
  overdue: 'bg-fi-signal-red',
  never: 'bg-fi-signal-slate',
};
export const PC_CAL_RAIL: Record<string, string> = {
  valid: 'border-l-fi-green',
  due_soon: 'border-l-fi-amber',
  overdue: 'border-l-fi-red',
  never: 'border-l-fi-steel',
};

/** A drill standing. Overdue is amber, not red: a drill warning never blocks anything. */
export const PC_DRILL_STATUS_LABEL: Record<string, string> = { current: 'Current', overdue: 'Overdue', never: 'No drill on record' };
export const PC_DRILL_STATUS_FILL: Record<string, string> = {
  current: 'bg-fi-signal-green',
  overdue: 'bg-fi-signal-amber',
  never: 'bg-fi-signal-slate',
};

/** The API's `daysUntilDue`, said. Negative is past the last valid day. */
export function pcDueDaysPhrase(days: number | null | undefined): string {
  if (days === null || days === undefined || !Number.isFinite(days)) return '—';
  if (days === 0) return 'Last valid day today';
  if (days > 0) return `${plural(days, 'day', 'days')} left`;
  return `${plural(-days, 'day', 'days')} past`;
}

/** A pressure with its unit — "34,474 kPa" — or `—`. The number is the API's, in the tenant's unit. */
export function pcPressure(value: number | null | undefined, unit: string | null | undefined): string {
  const n = fmtQty(value, 1);
  return n === '—' ? n : `${n}${unit ? ` ${unit}` : ''}`;
}

/** Minutes held, or `—`. */
export const pcMinutes = (m: number | null | undefined) =>
  m === null || m === undefined || !Number.isFinite(m) ? '—' : `${m} min`;

/** Seconds to secure, as "1 min 25 s" / "40 s", or `—`. */
export function pcSeconds(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m === 0) return `${r} s`;
  return r === 0 ? `${m} min` : `${m} min ${r} s`;
}

/** Who did the test: the named user, else the typed name, else `—`. */
export const pcTester = (t: { testedByUserName: string | null; testedByName: string | null }) =>
  t.testedByUserName || t.testedByName || '—';

const numOr = (o: Raw, camel: string, snake: string) => numOrNull(pick(o, camel, snake));

export function readCalibration(raw: Raw): PcCalibration | null {
  if (!raw || !raw.id) return null;
  const st = raw.state ?? {};
  return {
    id: raw.id,
    equipmentId: pick(raw, 'equipmentId', 'equipment_id') ?? null,
    calibratedOn: pick(raw, 'calibratedOn', 'calibrated_on') ?? null,
    dueOn: pick(raw, 'dueOn', 'due_on') ?? null,
    rangeMin: numOr(raw, 'rangeMin', 'range_min'),
    rangeMax: numOr(raw, 'rangeMax', 'range_max'),
    pressureUnit: pick(raw, 'pressureUnit', 'pressure_unit') ?? '',
    accuracyClass: orNull(pick(raw, 'accuracyClass', 'accuracy_class')),
    certificateNumber: orNull(pick(raw, 'certificateNumber', 'certificate_number')),
    performedBy: orNull(pick(raw, 'performedBy', 'performed_by')),
    notes: orNull(raw.notes),
    createdBy: orNull(pick(raw, 'createdBy', 'created_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
    state: {
      status: st.status ?? null,
      daysUntilDue: numOr(st, 'daysUntilDue', 'days_until_due'),
      alertTier: numOr(st, 'alertTier', 'alert_tier'),
    },
  };
}

export function readGaugeCalibration(raw: Raw): PcGaugeCalibration | null {
  if (!raw) return null;
  const history: any[] = Array.isArray(raw.history) ? raw.history : [];
  return {
    equipmentId: pick(raw, 'equipmentId', 'equipment_id') ?? null,
    status: raw.status ?? null,
    daysUntilDue: numOr(raw, 'daysUntilDue', 'days_until_due'),
    alertTier: numOr(raw, 'alertTier', 'alert_tier'),
    current: readCalibration(raw.current),
    history: history.map(readCalibration).filter(Boolean) as PcCalibration[],
  };
}

function readTestItem(raw: Raw): PcTestItem {
  const r = raw ?? {};
  return {
    id: r.id ?? null,
    equipmentId: pick(r, 'equipmentId', 'equipment_id') ?? null,
    name: orNull(r.name),
    componentClass: orNull(pick(r, 'componentClass', 'component_class')),
    lowTestPressure: numOr(r, 'lowTestPressure', 'low_test_pressure'),
    lowTestEntered: numOr(r, 'lowTestEntered', 'low_test_entered'),
    lowTestUnit: orNull(pick(r, 'lowTestUnit', 'low_test_unit')),
    lowHoldMinutes: numOr(r, 'lowHoldMinutes', 'low_hold_minutes'),
    highTestPressure: numOr(r, 'highTestPressure', 'high_test_pressure'),
    highTestEntered: numOr(r, 'highTestEntered', 'high_test_entered'),
    highTestUnit: orNull(pick(r, 'highTestUnit', 'high_test_unit')),
    highHoldMinutes: numOr(r, 'highHoldMinutes', 'high_hold_minutes'),
    result: r.result ?? null,
    notes: orNull(r.notes),
  };
}

export function readTest(raw: Raw): PcTest | null {
  if (!raw || !raw.id) return null;
  const g = raw.gauge ?? {};
  const items: any[] = Array.isArray(raw.items) ? raw.items : [];
  return {
    id: raw.id,
    testedAt: orNull(pick(raw, 'testedAt', 'tested_at')),
    testedOn: orNull(pick(raw, 'testedOn', 'tested_on')),
    testKind: pick(raw, 'testKind', 'test_kind') ?? null,
    stackEquipmentId: orNull(pick(raw, 'stackEquipmentId', 'stack_equipment_id')),
    stackName: orNull(pick(raw, 'stackName', 'stack_name')),
    gauge: {
      equipmentId: orNull(pick(g, 'equipmentId', 'equipment_id')),
      name: orNull(g.name),
      calibrationId: orNull(pick(g, 'calibrationId', 'calibration_id')),
      calibratedOn: orNull(pick(g, 'calibratedOn', 'calibrated_on')),
      dueOn: orNull(pick(g, 'dueOn', 'due_on')),
      rangeMax: numOr(g, 'rangeMax', 'range_max'),
    },
    pressureUnit: pick(raw, 'pressureUnit', 'pressure_unit') ?? '',
    chartUploadId: orNull(pick(raw, 'chartUploadId', 'chart_upload_id')),
    testedByUserId: orNull(pick(raw, 'testedByUserId', 'tested_by_user_id')),
    testedByUserName: orNull(pick(raw, 'testedByUserName', 'tested_by_user_name')),
    testedByName: orNull(pick(raw, 'testedByName', 'tested_by_name')),
    witnessedBy: orNull(pick(raw, 'witnessedBy', 'witnessed_by')),
    contextRef: orNull(pick(raw, 'contextRef', 'context_ref')),
    result: raw.result ?? null,
    notes: orNull(raw.notes),
    recordedBy: orNull(pick(raw, 'recordedBy', 'recorded_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
    reviewStatus: orNull(pick(raw, 'reviewStatus', 'review_status')),
    reviewedAt: orNull(pick(raw, 'reviewedAt', 'reviewed_at')),
    reviewedBy: orNull(pick(raw, 'reviewedBy', 'reviewed_by')),
    items: items.map(readTestItem),
  };
}

export function readDrill(raw: Raw): PcDrill | null {
  if (!raw || !raw.id) return null;
  const crew: any[] = Array.isArray(raw.crew) ? raw.crew : [];
  return {
    id: raw.id,
    drilledAt: orNull(pick(raw, 'drilledAt', 'drilled_at')),
    drilledOn: orNull(pick(raw, 'drilledOn', 'drilled_on')),
    drillKind: pick(raw, 'drillKind', 'drill_kind') ?? null,
    secondsToSecure: numOr(raw, 'secondsToSecure', 'seconds_to_secure'),
    rigEquipmentId: orNull(pick(raw, 'rigEquipmentId', 'rig_equipment_id')),
    rigName: orNull(pick(raw, 'rigName', 'rig_name')),
    stackEquipmentId: orNull(pick(raw, 'stackEquipmentId', 'stack_equipment_id')),
    stackName: orNull(pick(raw, 'stackName', 'stack_name')),
    contextRef: orNull(pick(raw, 'contextRef', 'context_ref')),
    notes: orNull(raw.notes),
    recordedBy: orNull(pick(raw, 'recordedBy', 'recorded_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
    reviewStatus: orNull(pick(raw, 'reviewStatus', 'review_status')),
    reviewedAt: orNull(pick(raw, 'reviewedAt', 'reviewed_at')),
    reviewedBy: orNull(pick(raw, 'reviewedBy', 'reviewed_by')),
    crew: crew.map((c) => ({ userId: pick(c, 'userId', 'user_id') ?? null, name: orNull(c?.name) })),
  };
}

function readStanding(raw: Raw): PcDrillStanding {
  const r = raw ?? {};
  return {
    id: r.id ?? null,
    name: orNull(r.name),
    status: r.status ?? null,
    lastDrilledOn: orNull(pick(r, 'lastDrilledOn', 'last_drilled_on')),
    daysSince: numOr(r, 'daysSince', 'days_since'),
    dueBy: orNull(pick(r, 'dueBy', 'due_by')),
  };
}

const readWarnings = (list: any): PcDrillWarning[] =>
  (Array.isArray(list) ? list : []).map((w: any) => ({
    kind: w?.kind ?? null, id: w?.id ?? null, status: w?.status ?? null, message: w?.message ?? '—',
  }));

export function readDrillStatus(raw: Raw): PcDrillStatus | null {
  if (!raw) return null;
  const nf = pick(raw, 'notFound', 'not_found') ?? {};
  return {
    warnDays: numOr(raw, 'warnDays', 'warn_days') as number,
    crew: (Array.isArray(raw.crew) ? raw.crew : []).map(readStanding),
    rigs: (Array.isArray(raw.rigs) ? raw.rigs : []).map(readStanding),
    notFound: { userIds: pick(nf, 'userIds', 'user_ids') ?? [], equipmentIds: pick(nf, 'equipmentIds', 'equipment_ids') ?? [] },
    warnings: readWarnings(raw.warnings),
  };
}

export function readTestDefault(raw: Raw): PcTestDefault | null {
  if (!raw || !raw.id) return null;
  return {
    id: raw.id,
    componentClass: orNull(pick(raw, 'componentClass', 'component_class')),
    equipmentId: orNull(pick(raw, 'equipmentId', 'equipment_id')),
    equipmentName: orNull(pick(raw, 'equipmentName', 'equipment_name')),
    lowTestPressure: numOr(raw, 'lowTestPressure', 'low_test_pressure'),
    highTestPressure: numOr(raw, 'highTestPressure', 'high_test_pressure'),
    pressureUnit: pick(raw, 'pressureUnit', 'pressure_unit') ?? '',
    lowHoldMinutes: numOr(raw, 'lowHoldMinutes', 'low_hold_minutes'),
    highHoldMinutes: numOr(raw, 'highHoldMinutes', 'high_hold_minutes'),
    updatedBy: orNull(pick(raw, 'updatedBy', 'updated_by')),
    updatedAt: orNull(pick(raw, 'updatedAt', 'updated_at')),
    ...(raw.source ? { source: raw.source } : {}),
  };
}

export function readExtension(raw: Raw): PcExtension | null {
  if (!raw || !raw.id) return null;
  return {
    id: raw.id,
    certificationId: pick(raw, 'certificationId', 'certification_id') ?? null,
    equipmentId: pick(raw, 'equipmentId', 'equipment_id') ?? null,
    baseExpiresOn: pick(raw, 'baseExpiresOn', 'base_expires_on') ?? null,
    extendedThrough: pick(raw, 'extendedThrough', 'extended_through') ?? null,
    grantedOn: pick(raw, 'grantedOn', 'granted_on') ?? null,
    reason: raw.reason ?? null,
    contextRef: pick(raw, 'contextRef', 'context_ref') ?? null,
    grantedBy: orNull(pick(raw, 'grantedBy', 'granted_by')),
    createdAt: orNull(pick(raw, 'createdAt', 'created_at')),
  };
}

export function readModuleStatus(raw: Raw): PcModuleStatus | null {
  if (!raw) return null;
  const comps = raw.components ?? {};
  const gauges: any[] = pick(raw, 'gaugesNeedingCalibration', 'gauges_needing_calibration') ?? [];
  const drills = raw.drills ?? {};
  return {
    components: {
      total: numOrNull(comps.total) as number,
      byStatus: pick(comps, 'byStatus', 'by_status') ?? {},
      extended: numOrNull(comps.extended) as number,
    },
    gaugesNeedingCalibration: (Array.isArray(gauges) ? gauges : []).map((g) => ({
      equipmentId: pick(g, 'equipmentId', 'equipment_id') ?? null,
      name: orNull(g?.name),
      dueOn: orNull(pick(g, 'dueOn', 'due_on')),
      status: g?.status ?? null,
      daysUntilDue: numOr(g, 'daysUntilDue', 'days_until_due'),
    })),
    drills: {
      warnDays: numOr(drills, 'warnDays', 'warn_days') as number,
      rigs: (Array.isArray(drills.rigs) ? drills.rigs : []).map(readStanding),
      warnings: readWarnings(drills.warnings),
    },
  };
}

/** Recording a pressure test or a crew drill — the PC_EVENT gate. */
export const canRecordPcTest = canRecordPcEvent;

/**
 * A refused test, in sentences: the API's own error, plus one line per refused
 * component when the 409 names them. A 403 is the permission sentence.
 */
export function pcTestRefusal(error: any, fallback: string): { message: string; failures: Array<{ equipmentId: string; error: string }> } {
  const message = pcErrorMessage(error, fallback);
  const raw = error?.response?.status === 403 ? null : error?.response?.data?.failures;
  const failures = Array.isArray(raw)
    ? raw.map((f: any) => ({ equipmentId: pick(f, 'equipmentId', 'equipment_id') ?? '', error: typeof f?.error === 'string' ? f.error : '—' }))
    : [];
  return { message, failures };
}
